import React, { useEffect, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { AppContainer, MainContent, theme } from './App.styles';
import './global-responsive.css';
import {
  getStoredUser,
  logout,
  setStoredRole,
  setStoredUser,
  subscribeApiActivity,
  subscribeApiErrors,
} from './components/api';

import LoginScreen from './components/Screens/LoginScreen';
import SetupWizard from './components/Screens/SetupWizard';
import AdminDashboard from './components/Screens/AdminDashboard';
import InventoryDashboard from './components/InventoryScreens/InventoryLayout';
import ProductionRouting from './components/ProductionDashboard/ProductionRouting';
import QCDashboardMain from './components/QCDashboard/QCDashboard';
import HRRouting from './components/HR/Hr_routing';
import SalesRouting from './components/SalesDashboard/SalesRouting';
import AccountsRouting from './components/Accounts/AccountsRouting';
import { canModule, hasAnyRole } from './utils/roleMatrix';

const MODULE_TO_SCREEN = {
  admin: 'screen-dashboard',
  inventory: 'screen-inventory-dashboard',
  production: 'screen-production-dashboard',
  qc: 'screen-qc-dashboard',
  hr: 'screen-hr-dashboard',
  sales: 'screen-sales-dashboard',
  accounts: 'screen-accounts',
};

const moduleLabel = (name) => `${String(name || '').charAt(0).toUpperCase()}${String(name || '').slice(1)}`;

const ROLE_TO_MODULE = {
  PRODUCTION_MANAGER: 'production',
  PRODUCTION_WORKER: 'production',
  INVENTORY_MANAGER: 'inventory',
  INVENTORY_ADMIN: 'inventory',
  STORE_MANAGER: 'inventory',
  QC_MANAGER: 'qc',
  QC_INSPECTOR: 'qc',
  HR_MANAGER: 'hr',
  HR_ADMIN: 'hr',
  HR_STAFF: 'hr',
  HR_EXECUTIVE: 'hr',
  SALES_MANAGER: 'sales',
  SALES_STAFF: 'sales',
  ACCOUNTANT: 'accounts',
  ACCOUNTS_MANAGER: 'accounts',
  ADMIN: 'admin',
  ORG_ADMIN: 'admin',
  SUPER_ADMIN: 'admin',
  OWNER: 'admin',
};

const roleToModule = (role) => {
  if (!role) return null;
  return ROLE_TO_MODULE[String(role).trim().toUpperCase()] || null;
};

const inferModuleFromDashboardRoute = (dashboardRoute) => {
  const route = String(dashboardRoute || '').toLowerCase();
  if (!route) return null;
  if (route.includes('/accountant') || route.includes('/accounts')) return 'accounts';
  if (route.includes('/inventory')) return 'inventory';
  if (route.includes('/production')) return 'production';
  if (route.includes('/qc')) return 'qc';
  if (route.includes('/hr')) return 'hr';
  if (route.includes('/sales')) return 'sales';
  if (route.includes('/admin')) return 'admin';
  return null;
};

const getAvailableModules = (user) => {
  const modules = [];
  Object.keys(MODULE_TO_SCREEN).forEach((moduleKey) => {
    if (canModule(user, moduleKey, 'read')) {
      modules.push(moduleKey);
    }
  });

  if (!modules.length && (user?.is_admin || hasAnyRole(user, ['ADMIN']))) {
    return Object.keys(MODULE_TO_SCREEN);
  }

  return modules;
};

const buildPickerOptions = (user) => {
  const roleOptions = Array.isArray(user?.role_options) ? user.role_options : [];
  if (roleOptions.length > 1) return roleOptions;

  const modules = getAvailableModules(user);
  const fallbackRole =
    user?.active_role ||
    roleOptions[0]?.role ||
    user?.roles?.[0] ||
    user?.access_matrix?.[0]?.role ||
    'ADMIN';

  return [{ role: fallbackRole, modules }];
};

const pickDefaultModule = (user, preferredRole = null) => {
  const roleOptions = Array.isArray(user?.role_options) ? user.role_options : [];
  const accessMatrix = Array.isArray(user?.access_matrix) ? user.access_matrix : [];
  const resolvedRole =
    preferredRole ||
    user?.active_role ||
    roleOptions[0]?.role ||
    user?.roles?.[0] ||
    accessMatrix[0]?.role ||
    null;

  const roleMappedModule = roleToModule(resolvedRole);
  const roleOption = roleOptions.find((opt) => opt?.role === resolvedRole) || roleOptions[0] || null;
  const optionModules = Array.isArray(roleOption?.modules) ? roleOption.modules.filter(Boolean) : [];

  if (roleMappedModule && optionModules.includes(roleMappedModule)) {
    return roleMappedModule;
  }

  const matchingMatrix = accessMatrix.find((entry) => entry?.role === resolvedRole) || accessMatrix[0] || null;
  const dashboardModule = inferModuleFromDashboardRoute(matchingMatrix?.dashboard_route);
  if (dashboardModule && optionModules.includes(dashboardModule)) {
    return dashboardModule;
  }

  if (roleMappedModule) {
    return roleMappedModule;
  }

  if (dashboardModule) {
    return dashboardModule;
  }

  return optionModules[0] || null;
};

function App() {
  const [apiBusy, setApiBusy] = useState(false);
  const [apiError, setApiError] = useState('');
  const [showRolePicker, setShowRolePicker] = useState(false);
  const [pickerOptions, setPickerOptions] = useState([]);
  const [pickerRole, setPickerRole] = useState('');
  const [pickerModule, setPickerModule] = useState('');

  const screenForModule = (moduleName) => MODULE_TO_SCREEN[moduleName] || 'screen-dashboard';

  const getDashboardScreen = (user, preferredModule = null) => {
    if (!user) return 'screen-login';

    if (preferredModule) {
      return screenForModule(preferredModule);
    }

    if (user.is_admin || hasAnyRole(user, ['ADMIN'])) {
      return 'screen-dashboard';
    }
    if (canModule(user, 'accounts', 'read')) return 'screen-accounts';
    if (canModule(user, 'inventory', 'read')) return 'screen-inventory-dashboard';
    if (canModule(user, 'production', 'read')) return 'screen-production-dashboard';
    if (canModule(user, 'qc', 'read')) return 'screen-qc-dashboard';
    if (canModule(user, 'hr', 'read')) return 'screen-hr-dashboard';
    if (canModule(user, 'sales', 'read')) return 'screen-sales-dashboard';

    // Fallback: derive module from access_matrix role when effective_permissions are all false
    const matrixRole = user?.access_matrix?.[0]?.role;
    const matrixModule = roleToModule(matrixRole);
    if (matrixModule) return screenForModule(matrixModule);

    return 'screen-dashboard';
  };

  const [currentScreen, setCurrentScreen] = useState(() => {
    const parsedUser = getStoredUser();
    if (parsedUser) {
      try {
        const moduleName = parsedUser?.active_module || null;
        return getDashboardScreen(parsedUser, moduleName);
      } catch (e) {
        return 'screen-login';
      }
    }
    return 'screen-login';
  });

  const [currentUser, setCurrentUser] = useState(() => {
    return getStoredUser();
  });

  const isAdminScreen = currentScreen === 'screen-dashboard';
  const isProductionScreen = currentScreen === 'screen-production-dashboard';

  const handleLoginSuccess = (user) => {
    const roleOptions = buildPickerOptions(user);
    const availableModules = getAvailableModules(user);
    const hasMultipleRoles = roleOptions.length > 1;
    const hasMultipleModules = availableModules.length > 1;

    if (hasMultipleRoles || hasMultipleModules) {
      const defaultRole = roleOptions[0]?.role || '';
      const defaultModule = pickDefaultModule({ ...user, role_options: roleOptions }, defaultRole) || availableModules[0] || '';
      const pendingUser = { ...user, active_role: defaultRole, active_module: defaultModule };
      setStoredUser(pendingUser);
      setStoredRole(defaultRole);
      setCurrentUser(pendingUser);
      setPickerOptions(roleOptions);
      setPickerRole(defaultRole);
      setPickerModule(defaultModule);
      setShowRolePicker(true);
      return;
    }

    // Derive role from role_options first, then access_matrix as fallback
    const accessMatrixRole = user?.access_matrix?.[0]?.role || null;
    const singleRole = roleOptions[0]?.role || user?.active_role || user?.roles?.[0] || accessMatrixRole || null;
    const singleModule = pickDefaultModule({ ...user, role_options: roleOptions }, singleRole) || availableModules[0] || null;
    const resolvedUser = { ...user, active_role: singleRole, active_module: singleModule };
    setStoredUser(resolvedUser);
    setStoredRole(singleRole);
    setCurrentUser(resolvedUser);
    setCurrentScreen(getDashboardScreen(resolvedUser, singleModule));
  };

  const handleLogout = () => {
    logout({ remote: true });
    setCurrentUser(null);
    setShowRolePicker(false);
    setCurrentScreen('screen-login');
  };

  const confirmRolePicker = () => {
    const moduleName = pickerModule || pickDefaultModule(currentUser, pickerRole);
    const updatedUser = {
      ...(currentUser || {}),
      active_role: pickerRole || null,
      active_module: moduleName,
    };
    setStoredRole(updatedUser.active_role || null);
    setStoredUser(updatedUser);
    setCurrentUser(updatedUser);
    setShowRolePicker(false);
    setCurrentScreen(getDashboardScreen(updatedUser, moduleName));
  };

  const handleNavigateToRegister = () => {
    setCurrentScreen('screen-wizard');
  };

  const handleFinalizeSetup = () => {
    setCurrentScreen('screen-login');
  };

  useEffect(() => {
    const unsubActivity = subscribeApiActivity((count) => setApiBusy(count > 0));
    const unsubError = subscribeApiErrors((payload) => {
      const msg = payload?.message || 'API request failed';
      setApiError(msg);
      window.setTimeout(() => {
        setApiError((prev) => (prev === msg ? '' : prev));
      }, 4500);
    });
    return () => {
      unsubActivity();
      unsubError();
    };
  }, []);

  useEffect(() => {
    const isZeroLikeValue = (value) => /^0+(?:\.0+)?$/.test(String(value ?? '').trim());
    const supportsZeroReplace = (target) => (
      target instanceof HTMLInputElement
      && !target.readOnly
      && !target.disabled
      && ['number', 'text', 'tel', 'search'].includes((target.type || 'text').toLowerCase())
    );

    const setNativeValue = (element, value) => {
      const prototype = Object.getPrototypeOf(element);
      const descriptor = Object.getOwnPropertyDescriptor(prototype, 'value');
      if (descriptor?.set) {
        descriptor.set.call(element, value);
      } else {
        element.value = value;
      }
    };

    const armZeroReplacement = (event) => {
      const target = event.target;
      if (!supportsZeroReplace(target)) return;
      if (!isZeroLikeValue(target.value)) {
        delete target.dataset.zeroReplaceArmed;
        return;
      }
      target.dataset.zeroReplaceArmed = '1';
    };

    const clearZeroReplacement = (event) => {
      const target = event.target;
      if (target instanceof HTMLElement && target.dataset) {
        delete target.dataset.zeroReplaceArmed;
      }
    };

    const replaceLeadingZero = (event) => {
      const target = event.target;
      if (!supportsZeroReplace(target)) return;
      if (target.dataset.zeroReplaceArmed !== '1') return;
      if (!isZeroLikeValue(target.value)) {
        delete target.dataset.zeroReplaceArmed;
        return;
      }
      if (event.isComposing) return;
      if (event.inputType !== 'insertText') return;
      const inserted = String(event.data || '');
      if (!inserted) return;
      if (!/^[0-9.]$/.test(inserted)) return;

      event.preventDefault();
      setNativeValue(target, inserted);
      target.dispatchEvent(new Event('input', { bubbles: true }));
      window.requestAnimationFrame(() => {
        try {
          const nextPos = target.value.length;
          target.setSelectionRange(nextPos, nextPos);
        } catch (_) {
          // Some input types do not support setSelectionRange.
        }
      });
      delete target.dataset.zeroReplaceArmed;
    };

    document.addEventListener('focusin', armZeroReplacement, true);
    document.addEventListener('beforeinput', replaceLeadingZero, true);
    document.addEventListener('input', clearZeroReplacement, true);
    document.addEventListener('blur', clearZeroReplacement, true);

    return () => {
      document.removeEventListener('focusin', armZeroReplacement, true);
      document.removeEventListener('beforeinput', replaceLeadingZero, true);
      document.removeEventListener('input', clearZeroReplacement, true);
      document.removeEventListener('blur', clearZeroReplacement, true);
    };
  }, []);

  const renderScreen = () => {
    switch (currentScreen) {
      case 'screen-login':
        return <LoginScreen onLoginSuccess={handleLoginSuccess} onSwitchToRegister={handleNavigateToRegister} />;
      case 'screen-wizard':
        return <SetupWizard onFinalize={handleFinalizeSetup} />;
      case 'screen-dashboard':
        return <AdminDashboard user={currentUser} onLogout={handleLogout} />;
      case 'screen-inventory-dashboard':
        return <InventoryDashboard user={currentUser} onLogout={handleLogout} />;
      case 'screen-production-dashboard':
        return <ProductionRouting user={currentUser} onLogout={handleLogout} />;
      case 'screen-qc-dashboard':
        return <QCDashboardMain user={currentUser} onLogout={handleLogout} />;
      case 'screen-hr-dashboard':
        return <HRRouting user={currentUser} onLogout={handleLogout} />;
      case 'screen-account-dashboard':
      case 'screen-accounts':
        return <AccountsRouting role={currentUser?.role} user={currentUser} onLogout={handleLogout} />;
      case 'screen-sales-dashboard':
        return <SalesRouting user={currentUser} onLogout={handleLogout} />;
      default:
        return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <AppContainer>
        {!isProductionScreen && apiBusy && (
          <div className="api-global-overlay">
            <div className="api-global-overlay-card">
              <span className="api-global-spinner" />
              <span>Loading data...</span>
            </div>
          </div>
        )}
        {apiError && <div className="api-global-error">{apiError}</div>}
        {showRolePicker && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ width: '100%', maxWidth: 520, background: '#ffffff', borderRadius: 12, border: '1px solid #e5e7eb', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', padding: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: '#111827' }}>Choose Role And Module</h3>
              <p style={{ marginTop: 8, marginBottom: 16, fontSize: 13, color: '#6b7280' }}>
                Select the module you want to open for this login session.
              </p>
              <div style={{ display: 'grid', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>Role</label>
                  <select
                    value={pickerRole}
                    onChange={(e) => {
                      const roleValue = e.target.value;
                      setPickerRole(roleValue);
                      const firstModule = pickerOptions.find((x) => x.role === roleValue)?.modules?.[0] || '';
                      setPickerModule(firstModule);
                    }}
                    style={{ width: '100%', marginTop: 6, padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}
                  >
                    {pickerOptions.map((opt) => (
                      <option key={opt.role} value={opt.role}>{opt.role}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>Module</label>
                  <select
                    value={pickerModule}
                    onChange={(e) => setPickerModule(e.target.value)}
                    style={{ width: '100%', marginTop: 6, padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}
                  >
                    {(pickerOptions.find((x) => x.role === pickerRole)?.modules || []).map((mod) => (
                      <option key={mod} value={mod}>{moduleLabel(mod)}</option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={confirmRolePicker}
                  style={{ minHeight: 44, border: 0, borderRadius: 8, background: '#2563eb', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
        <MainContent
          isLoginScreen={currentScreen === 'screen-login' || currentScreen === 'screen-wizard'}
          isWorkspaceScreen={
            currentScreen === 'screen-account-dashboard' ||
            currentScreen === 'screen-accounts' ||
            isAdminScreen
          }
        >
          {renderScreen()}
        </MainContent>
      </AppContainer>
    </ThemeProvider>
  );
}

export default App;
